import { useState, useMemo, useEffect } from 'react'
import {
  ToolPage,
  ToolCard,
  ToolFieldRow,
  ToolNumberField,
  ToolResultCard,
  ToolStatRows,
} from '@/components/tools/ToolKit'

const BENCHMARKS = [
  { label: 'E-commerce', range: '1–4%' },
  { label: 'SaaS landing pages', range: '3–8%' },
  { label: 'B2B', range: '2–5%' },
]

type Inputs = { visitors: string; conversions: string; desiredRate: string; reverseVisitors: string }

/** `initial` only seeds the dev states gallery; the route renders it empty. */
export default function ConversionRateCalculator({ initial }: { initial?: Inputs } = {}) {
  const [visitors, setVisitors] = useState(initial?.visitors ?? '')
  const [conversions, setConversions] = useState(initial?.conversions ?? '')
  const [desiredRate, setDesiredRate] = useState(initial?.desiredRate ?? '')
  const [reverseVisitors, setReverseVisitors] = useState(initial?.reverseVisitors ?? '')

  useEffect(() => {
    document.title = 'Conversion Rate Calculator - Humblytics'
  }, [])

  const conversionRate = useMemo(() => {
    const v = parseInt(visitors)
    const c = parseInt(conversions)
    if (!v || v <= 0 || isNaN(c) || c < 0) return null
    if (c > v) return null
    return (c / v) * 100
  }, [visitors, conversions])

  const requiredConversions = useMemo(() => {
    const rate = parseFloat(desiredRate)
    const v = parseInt(reverseVisitors)
    if (!rate || rate <= 0 || rate > 100 || !v || v <= 0) return null
    return Math.ceil((rate / 100) * v)
  }, [desiredRate, reverseVisitors])

  const currentConversions = parseInt(conversions)
  const gap = requiredConversions !== null && !isNaN(currentConversions) ? requiredConversions - currentConversions : null

  const targetRows =
    requiredConversions === null
      ? []
      : [
          { label: `Needed for ${parseFloat(desiredRate)}%`, value: `${requiredConversions.toLocaleString()} conversions` },
          ...(gap !== null ? [{ label: 'Gap from today', value: gap > 0 ? `${gap.toLocaleString()} more` : 'Already there' }] : []),
        ]

  const inputs = (
    <>
      <ToolCard title="Your numbers">
        <ToolFieldRow>
          <ToolNumberField id="visitors" label="Total visitors" placeholder="e.g. 12400" value={visitors} onChange={setVisitors} />
          <ToolNumberField id="conversions" label="Total conversions" placeholder="e.g. 434" value={conversions} onChange={setConversions} />
        </ToolFieldRow>
      </ToolCard>
      <ToolCard title="Conversions needed for a target">
        <ToolFieldRow>
          <ToolNumberField id="desired-rate" label="Target rate (%)" placeholder="e.g. 5" max={100} step={0.1} value={desiredRate} onChange={setDesiredRate} />
          <ToolNumberField id="reverse-visitors" label="Expected visitors" placeholder="e.g. 12400" value={reverseVisitors} onChange={setReverseVisitors} />
        </ToolFieldRow>
      </ToolCard>
    </>
  )

  const aside = (
    <ToolResultCard
      title="Conversion rate"
      value={conversionRate !== null ? `${conversionRate.toFixed(2)}%` : undefined}
      caption={
        conversionRate !== null
          ? `${currentConversions.toLocaleString()} of ${parseInt(visitors).toLocaleString()} visitors converted.`
          : undefined
      }
      empty="Enter total visitors and conversions to see your rate."
    >
      {targetRows.length > 0 && <ToolStatRows rows={targetRows} />}
      <div className="flex flex-col gap-2 rounded-lg bg-canvas px-3.5 py-3">
        <p className="text-xs font-medium leading-4 text-foreground">Typical ranges</p>
        {BENCHMARKS.map((b) => (
          <div key={b.label} className="flex justify-between text-xs leading-4">
            <span className="text-foreground/60">{b.label}</span>
            <span className="text-foreground">{b.range}</span>
          </div>
        ))}
      </div>
    </ToolResultCard>
  )

  return (
    <ToolPage
      title="Conversion rate calculator"
      description="Work out a conversion rate, or the conversions you need to hit a target."
      inputs={inputs}
      aside={aside}
    />
  )
}
